import type React from 'react'
import type { LinkableTextFrame } from '@/lib/text-linking/types/linkable-text-frame'

/**
 * Frame-body HIT areas for an active linking session — rendered LAST by the layer, so they
 * hit-test above every in-port square.
 *
 * Why this needs its own pass: an in-port square is `PORT_HIT_SIZE` screen px, centred on
 * the frame's top-edge midpoint, so by construction half of it lies OUTSIDE its own frame
 * (`text-linking-geometry.ts`). While the body rects painted first, every frame's port
 * square outranked every other frame's body — so at low zoom, where that square covers a
 * lot of document space, a text box sitting near a shape swallowed the click meant for the
 * shape and linked itself instead (WMPCM-9070 Bug 3).
 *
 * Containment is the stronger signal: a click inside a frame links THAT frame. A port square
 * still works over the part of itself that sticks out past its frame, which is what it is for.
 * A frame's own port and its own body both call `onInPortClick(frame.id)`, so the two
 * overlapping is a no-op either way.
 *
 * Trade-off, accepted: these also cover another frame's out-port (unlink) square during a
 * linking session, so mid-gesture unlinking now links instead. Unlink remains fully reachable
 * in its normal state — `linkingActive` is false there and no body hit rect renders at all.
 *
 * `aria-hidden`: these duplicate the in-port square's action for pointer convenience only.
 * The port square is the accessible control, so exposing these too would put two tab stops
 * and two identical buttons on every frame.
 *
 * Deliberately the frame's own raw (geometric, unrotated) rect projected by `scale`, not
 * `chromeFrameOf`'s visual rect — same as before this layer moved to screen space; changing
 * that is a separate concern from where this layer draws. The 2px pad is a literal screen
 * margin, same convention selection chrome uses for its own such paddings.
 */
export function FrameBodyHitAreas({
  portFrames,
  sourceId,
  scale,
  onInPortClick,
  onPortHoverChange,
}: {
  portFrames: LinkableTextFrame[]
  sourceId: string | undefined
  scale: number
  onInPortClick: (frameId: string) => void
  onPortHoverChange?: (frameId: string | null) => void
}): React.JSX.Element {
  return (
    <>
      {portFrames.map((frame) =>
        frame.id === sourceId ? null : (
          <rect
            key={`body-hit-${frame.id}`}
            x={frame.x * scale - 2}
            y={frame.y * scale - 2}
            width={frame.width * scale + 4}
            height={frame.height * scale + 4}
            // `transparent`, not `none`: under the default `visiblePainted` hit-testing an unfilled
            // rect is only a target where its stroke paints, so the whole interior of this "click
            // me to link" affordance was inert — no hover feedback and no click, and the click then
            // fell through to the pasteboard, which read it as empty canvas (WMPCM-9070). Same
            // trick `PortSquare` already uses for its hit area.
            fill="transparent"
            stroke="none"
            style={{ pointerEvents: 'auto' }}
            data-text-link-port="true"
            aria-hidden="true"
            onPointerDown={(e) => e.stopPropagation()}
            onMouseEnter={() => onPortHoverChange?.(frame.id)}
            onMouseLeave={() => onPortHoverChange?.(null)}
            onClick={() => onInPortClick(frame.id)}
            cursor="pointer"
          />
        )
      )}
    </>
  )
}
